import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Briefcase, Trash2, Plus, Edit, X, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default services list (matches what's on the frontend)
const DEFAULT_SERVICES = [
  { id: 'svc-1', title: 'Performances', description: 'Live performances that blend Indigenous culture with contemporary artistry. From music to dance, we bring powerful, authentic entertainment to your stage.', icon: 'Mic2', display_order: 1 },
  { id: 'svc-2', title: 'Fashion Show Productions', description: 'Full-service fashion show production from concept to runway. We handle casting, choreography, styling, and event coordination for unforgettable shows.', icon: 'Theater', display_order: 2 },
  { id: 'svc-3', title: 'Workshops', description: 'Hands-on creative workshops covering fashion design, music production, visual arts, and cultural storytelling. Perfect for community centers and schools.', icon: 'Users', display_order: 3 },
  { id: 'svc-4', title: 'Youth Seminars', description: 'Empowering the next generation through seminars focused on entrepreneurship, creative careers, cultural identity, and self-expression.', icon: 'GraduationCap', display_order: 4 },
  { id: 'svc-5', title: 'Digital Storytelling / Documentaries', description: 'Capturing authentic stories through documentary filmmaking. We preserve and share Indigenous narratives with compelling visual storytelling.', icon: 'Film', display_order: 5 },
  { id: 'svc-6', title: 'Music Recording Sessions', description: 'Professional music production and recording services. From hip-hop to traditional fusion, we help artists bring their sound to life.', icon: 'Headphones', display_order: 6 },
  { id: 'svc-7', title: 'Photography / Videography', description: 'High-quality photo and video production for events, brands, and personal projects. We capture moments that tell your story.', icon: 'Camera', display_order: 7 },
  { id: 'svc-8', title: 'Web Design', description: 'Custom website design and development that reflects your brand\'s unique identity. Modern, responsive, and built to convert.', icon: 'Globe', display_order: 8 },
  { id: 'svc-9', title: 'Graphic Design', description: 'Eye-catching graphics for logos, merchandise, marketing materials, and social media. Design that makes your brand stand out.', icon: 'Palette', display_order: 9 },
  { id: 'svc-10', title: 'Brand Consulting', description: 'Strategic brand development and consulting to help you define your identity, reach your audience, and grow your presence.', icon: 'Briefcase', display_order: 10 },
  { id: 'svc-11', title: 'Clothing Printing', description: 'Custom apparel printing services for merchandise, uniforms, and promotional items. Quality prints that represent your brand.', icon: 'Shirt', display_order: 11 },
  { id: 'svc-12', title: 'Collaborations', description: 'Partnership opportunities for brands, artists, and organizations looking to create something meaningful together.', icon: 'Handshake', display_order: 12 },
  { id: 'svc-13', title: 'Speaking Gigs', description: 'Inspirational keynotes and panel discussions on Indigenous entrepreneurship, creative industries, and cultural empowerment.', icon: 'MessageSquare', display_order: 13 },
];

const ICON_OPTIONS = [
  'Mic2', 'Theater', 'Users', 'GraduationCap', 'Film', 'Headphones', 
  'Camera', 'Globe', 'Palette', 'Briefcase', 'Shirt', 'Handshake', 'MessageSquare'
];

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'Briefcase'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/services`);
      const data = await response.json();
      
      // If no services in DB, use defaults
      if (data.length === 0) {
        setServices(DEFAULT_SERVICES);
      } else {
        setServices(data);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setServices(DEFAULT_SERVICES);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', icon: 'Briefcase' });
    setEditingService(null);
    setShowForm(false);
  };

  const handleEdit = (service) => {
    setFormData({
      title: service.title || '',
      description: service.description || '',
      icon: service.icon || 'Briefcase'
    });
    setEditingService(service);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingService) {
        await fetch(`${API_URL}/api/admin/services/${editingService.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
      } else {
        await fetch(`${API_URL}/api/admin/services`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: `svc-${Date.now()}`,
            ...formData,
            display_order: services.length + 1,
            is_active: true
          })
        });
      }

      await fetchServices();
      resetForm();
    } catch (err) {
      console.error('Error saving service:', err);
      alert('Failed to save service');
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await fetch(`${API_URL}/api/admin/services/${id}`, {
        method: 'DELETE'
      });
      await fetchServices();
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Failed to delete service');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Services Management</h1>
            <p className="text-gray-400">Edit your service offerings</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </CardTitle>
                <button onClick={resetForm} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-white mb-2 text-sm">Service Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 text-sm">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 text-sm">Icon</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  >
                    {ICON_OPTIONS.map((icon) => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-black w-full">
                  <Save className="w-4 h-4 mr-2" />
                  {editingService ? 'Update Service' : 'Create Service'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Services List */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading services...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <Card key={service.id} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold mb-1">{service.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{service.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleEdit(service)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => deleteService(service.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Note */}
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-sm">
              <strong className="text-orange-500">Note:</strong> Changes to services will update the "Our Services" section on your website. 
              The icons are displayed using the selected icon name. Currently, icon changes require a code update to take effect.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminServices;
